import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { GripHorizontal, Palette, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const SIZE = { width: 232, height: 208 };

// Cores pensadas para ler bem nos dois temas; o texto é sempre escuro, como
// num autocolante de papel.
const COLORS = {
  amarelo: "bg-amber-200 border-amber-300",
  lilas: "bg-violet-200 border-violet-300",
  verde: "bg-emerald-200 border-emerald-300",
  azul: "bg-sky-200 border-sky-300",
  rosa: "bg-pink-200 border-pink-300",
};
const COLOR_ORDER = Object.keys(COLORS);

function clamp({ x, y }) {
  const maxX = Math.max(8, window.innerWidth - SIZE.width - 8);
  const maxY = Math.max(8, window.innerHeight - SIZE.height - 8);
  return { x: Math.min(Math.max(8, x), maxX), y: Math.min(Math.max(8, y), maxY) };
}

/** Procura um sítio livre para a nota nova não cair por cima de outra. */
function freeSpot(notes) {
  const step = 28;
  for (let index = 0; index < 40; index += 1) {
    const candidate = clamp({ x: 96 + index * step, y: 140 + index * step });
    const taken = notes.some((note) => Math.abs(note.x - candidate.x) < 24 && Math.abs(note.y - candidate.y) < 24);
    if (!taken) return candidate;
  }
  return clamp({ x: 96, y: 140 });
}

function Note({ note, onChange, onDelete, onFocus, active }) {
  const [content, setContent] = useState(note.content || "");
  const [position, setPosition] = useState({ x: note.x, y: note.y });
  const dragOffset = useRef(null);
  const saveTimer = useRef(null);

  useEffect(() => { setContent(note.content || ""); }, [note.content]);
  useEffect(() => { setPosition({ x: note.x, y: note.y }); }, [note.x, note.y]);

  // Escreve-se, pára-se um segundo, fica guardado. Sem botão de guardar.
  const scheduleSave = (value) => {
    setContent(value);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => onChange({ ...note, ...position, content: value }), 800);
  };
  useEffect(() => () => clearTimeout(saveTimer.current), []);

  const onPointerMove = useCallback((event) => {
    if (!dragOffset.current) return;
    setPosition(clamp({ x: event.clientX - dragOffset.current.x, y: event.clientY - dragOffset.current.y }));
  }, []);

  const endDrag = useCallback(() => {
    if (!dragOffset.current) return;
    dragOffset.current = null;
    setPosition((current) => {
      onChange({ ...note, content, ...current });
      return current;
    });
  }, [note, content, onChange]);

  useEffect(() => {
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", endDrag);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", endDrag);
    };
  }, [onPointerMove, endDrag]);

  const nextColor = () => {
    const index = COLOR_ORDER.indexOf(note.color);
    const color = COLOR_ORDER[(index + 1) % COLOR_ORDER.length];
    onChange({ ...note, ...position, content, color });
  };

  return (
    <div
      className={`fixed flex flex-col rounded-lg border shadow-lg ${COLORS[note.color] || COLORS.amarelo} ${active ? "z-50" : "z-40"}`}
      style={{ left: position.x, top: position.y, width: SIZE.width, height: SIZE.height }}
      onPointerDown={() => onFocus(note.id)}
      data-testid={`sticky-note-${note.id}`}
    >
      <div
        onPointerDown={(event) => { dragOffset.current = { x: event.clientX - position.x, y: event.clientY - position.y }; }}
        className="flex cursor-grab items-center gap-1 px-2 py-1 active:cursor-grabbing"
      >
        <GripHorizontal className="h-3.5 w-3.5 text-black/40" />
        <div className="ml-auto flex items-center gap-1">
          <button onClick={nextColor} className="text-black/40 hover:text-black/70" aria-label="Mudar cor">
            <Palette className="h-3.5 w-3.5" />
          </button>
          <button onClick={() => onDelete(note.id)} className="text-black/40 hover:text-red-700" aria-label="Eliminar nota">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <textarea
        value={content}
        onChange={(event) => scheduleSave(event.target.value)}
        onBlur={() => onChange({ ...note, ...position, content })}
        placeholder="Escreve aqui..."
        className="flex-1 resize-none bg-transparent px-3 pb-3 font-roboto text-sm text-neutral-900 placeholder:text-black/35 focus:outline-none"
      />
    </div>
  );
}

/**
 * Notas autocolantes pessoais espalhadas pelo ecrã. Ficam guardadas no
 * servidor, com cor e posição, por isso aparecem iguais em qualquer
 * dispositivo.
 */
export function StickyNotes({ open, onClose }) {
  const qc = useQueryClient();
  const [activeId, setActiveId] = useState(null);

  const { data: notes = [] } = useQuery({
    queryKey: ["notes"],
    queryFn: async () => (await api.get("/notes")).data,
    enabled: open,
  });

  const fail = (error) => toast.error(apiError(error.response?.data?.detail));
  const refresh = () => qc.invalidateQueries({ queryKey: ["notes"] });

  const create = useMutation({
    mutationFn: async (note) => (await api.post("/notes", note)).data,
    onSuccess: refresh,
    onError: fail,
  });
  const update = useMutation({
    mutationFn: async (note) => (await api.put(`/notes/${note.id}`, note)).data,
    // Guardar não deve mexer no que está no ecrã: a nota já mostra o que foi
    // escrito, e recarregar aqui faria o cursor saltar.
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/notes/${id}`)).data,
    onSuccess: refresh,
    onError: fail,
  });

  if (!open) return null;

  return (
    <>
      <div className="fixed bottom-4 left-1/2 z-50 flex -translate-x-1/2 items-center gap-2 rounded-full border border-border bg-card px-3 py-2 shadow-xl">
        <Button
          size="sm"
          className="h-8 rounded-full bg-primary text-black hover:bg-primary/90"
          onClick={() => create.mutate({ content: "", color: "amarelo", ...freeSpot(notes) })}
          data-testid="note-new"
        >
          <Plus className="mr-1 h-4 w-4" /> Nova nota
        </Button>
        <span className="text-xs text-muted-foreground">
          {notes.length === 1 ? "1 nota" : `${notes.length} notas`}
        </span>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Fechar notas">
          <X className="h-4 w-4" />
        </button>
      </div>

      {notes.map((note) => (
        <Note
          key={note.id}
          note={note}
          active={activeId === note.id}
          onFocus={setActiveId}
          onChange={(value) => update.mutate(value)}
          onDelete={(id) => remove.mutate(id)}
        />
      ))}
    </>
  );
}
